import type { ReactElement } from 'react';
import React from 'react';
import {
  Table,
  Tbody,
  Thead,
  Box,
  Grid,
  Flex,
  designTokens,
  PaginationWithCounter
} from '@itandi/itandi-bb-ui';
import type {
  DealIndexVendor,
  DealSearchCondition,
  DealSortKey
} from '~/models/Deal';
import { useDynamicHeight } from '~/hooks/useDynamicHeight';
import { DealTableHeader } from './DealTableHeader';
import { DealTableRow } from './DealTableRow';
import { DealTableSPHeader } from './DealTableSPHeader';
import { DealTableSPRow } from './DealTableSPRow';

type Props = {
  deals: DealIndexVendor[];
  dealSearchCondition: DealSearchCondition;
  handleChangeCondition: (newCondition: DealSearchCondition) => Promise<void>;
  total: number;
  isSp: boolean;
  statusNames: { [key: number]: string };
};

export const DealTable: React.FC<Props> = ({
  deals,
  dealSearchCondition,
  handleChangeCondition,
  statusNames,
  total,
  isSp
}: Props) => {
  const nowSortKey = dealSearchCondition.sortKey || 'updated_date';
  const nowSortDirection = dealSearchCondition.sortDirection || 'desc';
  const sortCondition = React.useCallback(
    (sortKey: DealSortKey): 'asc' | 'desc' | 'none' =>
      sortKey === nowSortKey ? nowSortDirection : 'none',
    [nowSortKey, nowSortDirection]
  );
  const { ref: tableBoxRef, height: tableHeight } =
    useDynamicHeight<HTMLDivElement>();
  const handleSort =
    (selectedSortKey: DealSortKey) => async (): Promise<void> => {
      const sortDirection: 'asc' | 'desc' =
        selectedSortKey === nowSortKey && nowSortDirection === 'desc'
          ? 'asc'
          : 'desc';
      const newCondition: DealSearchCondition = {
        ...dealSearchCondition,
        sortKey: selectedSortKey,
        sortDirection
      };
      await handleChangeCondition(newCondition);
    };
  const [currentPageNum, setCurrentPageNum] = React.useState<number>(
    dealSearchCondition.page || 1
  );
  const handleChangePage = async (newPageNumber: number): Promise<void> => {
    const newCondition: DealSearchCondition = {
      ...dealSearchCondition,
      page: newPageNumber
    };
    setCurrentPageNum(newPageNumber);
    await handleChangeCondition(newCondition);
  };
  return (
    <>
      {!isSp && (
        <Grid
          ref={tableBoxRef}
          gridTemplateRows="1fr auto"
          maxHeight={tableHeight}
          minWidth="1200px"
        >
          <Box
            czz={{
              whiteSpace: 'nowrap',
              overflow: 'scroll'
            }}
          >
            <Table fixedColumnIndexOnLeft={1} fixedHeader>
              <Thead>
                <DealTableHeader
                  handleSort={handleSort}
                  sortCondition={sortCondition}
                />
              </Thead>
              <Tbody>
                {deals.map(
                  (deal: DealIndexVendor): ReactElement => (
                    <DealTableRow
                      key={deal.id}
                      deal={deal}
                      statusNames={statusNames}
                    />
                  )
                )}
              </Tbody>
            </Table>
          </Box>
          <Flex
            czz={{
              borderTop: `2px solid ${designTokens['color/border/subtle']}`
            }}
            justifyContent="center"
            paddingY={8}
          >
            <PaginationWithCounter
              currentPageNum={currentPageNum}
              itemsPerPage={50}
              onPageChange={handleChangePage}
              totalItems={total}
            />
          </Flex>
        </Grid>
      )}
      {isSp && (
        <>
          <Table fixedHeader>
            <Thead>
              <DealTableSPHeader
                handleSort={handleSort}
                sortCondition={sortCondition}
              />
            </Thead>
            <Tbody>
              {deals.map(
                (deal: DealIndexVendor): ReactElement => (
                  <DealTableSPRow
                    key={deal.id}
                    deal={deal}
                    statusNames={statusNames}
                  />
                )
              )}
            </Tbody>
          </Table>
          <Box
            bottom={0}
            czz={{
              borderTop: `2px solid ${designTokens['color/border/subtle']}`,
              backgroundColor: designTokens['color/primary/3']
            }}
            position="sticky"
            zIndex={1}
          >
            <Flex justifyContent="center" marginY={10}>
              <PaginationWithCounter
                currentPageNum={currentPageNum}
                itemsPerPage={50}
                onPageChange={handleChangePage}
                totalItems={total}
              />
            </Flex>
          </Box>
        </>
      )}
    </>
  );
};
